import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {Card, Form, Input, Alert, message, Modal, Select, Row, Col, Steps, Descriptions, DatePicker, Button, Popconfirm, Space, Dropdown, Anchor} from 'antd';
import { PlusOutlined, DownloadOutlined, UploadOutlined, FileExcelOutlined } from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import dayjs from 'dayjs';
import orderService, {FilterOption, Order, OrderListParams} from '../services/order';
import { getApiResponseErrorMessage, getAxiosErrorMessage } from '../utils/error';
import {SERVICE_TYPES} from '../components/ServiceTypeButtons';
import DynamicOrderForm from '../components/DynamicOrderForm';
import {SERVICE_TYPE_MAP} from '../config/service-type-map';
import {SERVICE_COLUMNS_MAP, getServiceColumnsUnion} from '../config/order-columns-config';
import {buildAllColumnDefinitions} from '../config/order-columns-definitions';
import { getStatusOptionsBySubService } from '../config/status-options';
import { NATION_OPTIONS } from '../config/nations';
import {CommissionCalculator} from '../utils/commissionCalculator';
import trademarkService from '../services/trademark';
import userPreferenceService from '../services/userPreference';
import api from '../services/api';
import userService from '../services/user';
import { optionService } from '../services/option';
import ColumnSelector from '../components/ColumnSelector';
import ImportOrdersModal from '../components/ImportOrdersModal';
import SubServiceFilter from '../components/orders/SubServiceFilter';
import OrdersToolbar from '../components/orders/OrdersToolbar';
import { usePermissions } from '../hooks/usePermissions';
import OrdersTable from '../components/orders/OrdersTable';
import FilterSummary from '../components/orders/FilterSummary';
import ListHelpButton from '../components/ListHelpButton';
import {useOrderFilters} from '../hooks/useOrderFilters';
import {useOrderPagination} from '../hooks/useOrderPagination';
import {useOrders} from '../hooks/useOrders';
import {useOrdersEffects} from '../hooks/useOrdersEffects';
import useOrderStatusOptions from '../hooks/useOrderStatusOptions';
import { useAuth } from '../contexts/AuthContext';
import useDocumentTitle from '../hooks/useDocumentTitle';
import SERVICE_TYPE_OPTIONS from '../config/service-types';
import MediaSelector from '../components/MediaLibrary/MediaSelector';
import {generateOrderNo, getOrderPrefix, generateOrderNoWithPrefix} from '../config/order-no-prefix';

const OrderManagement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // 数据与加载状态由 useOrders 提供
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [form] = Form.useForm();
  const [batchEditForm] = Form.useForm();
  const { searchText, setSearchText, searchField, setSearchField, filters, handleFilterChange, handleResetFilters } = useOrderFilters();
  const [selectedServiceType, setSelectedServiceType] = useState<string>('');
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [batchEditModalVisible, setBatchEditModalVisible] = useState(false);
  const [clientTypeOptions, setClientTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [sourceOptions, setSourceOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [batchSelectedNations, setBatchSelectedNations] = useState<string[]>([]);
  const [batchTab, setBatchTab] = useState<string>('parent');
  const [batchForm] = Form.useForm();
  const batchModalContentRef = useRef<HTMLDivElement | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  // 已移除“跟随默认列”开关，列显示优先级：用户列设置 > 子业务默认列 > 服务全量列
  // 子业务筛选（仅商标使用）
  const [subServiceFilter, setSubServiceFilter] = useState<string>(() => searchParams.get('sub_service') || '');
  const [subServiceOptions, setSubServiceOptions] = useState<{ value: string; label: string }[]>([]);

  // 处理子业务筛选：同步到 URL，支持刷新后保留
  const handleSubServiceChange = useCallback((val: string) => {
    setSubServiceFilter(val);
    const params = new URLSearchParams(location.search);
    if (val) params.set('sub_service', val); else params.delete('sub_service');
    setSearchParams(params, { replace: true });
  }, [location.search, setSearchParams]);

  // 分页状态（含 URL 同步）
  const { pagination, setPagination, updateUrlParams } = useOrderPagination(searchParams, setSearchParams);
  const [sortConfigState, setSortConfig] = useState<{
    field?: string;
    order?: 'ascend' | 'descend';
  }>({});

  // 使用 useMemo 稳定 sortConfig 对象引用
  const sortConfig = useMemo(() => sortConfigState, [
    sortConfigState.field,
    sortConfigState.order,
  ]);
  // 过滤条件已由 useOrderFilters 提供

  // 监控状态现在直接从订单数据的 is_monitored 字段获取，无需单独状态管理

  // 动态筛选选项
  const [filterOptions, setFilterOptions] = useState({
    nations: [] as FilterOption[],
    services: [] as FilterOption[],
    payment_statuses: [] as FilterOption[],
    applicant_names: [] as FilterOption[],
    contact_names: [] as FilterOption[],
    channels: [] as FilterOption[],
    sources: [] as FilterOption[],
    statuses: [] as FilterOption[],
    sellers: [] as FilterOption[],
  });
  const [sellerOptions, setSellerOptions] = useState<{ value: string | number; label: string }[]>([]);

  // 从URL获取业务类型参数，使用 useMemo 稳定引用
  const serviceFilter = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('service') || '';
  }, [location.search]);

  // 动态页面标题：`<子服务> - 业务订单 - <应用名>`
  const serviceLabel = useMemo(() => {
    const found = SERVICE_TYPE_OPTIONS.find(s => s.value === serviceFilter);
    return found?.label;
  }, [serviceFilter]);
  useDocumentTitle(`${serviceLabel ? serviceLabel + ' - ' : ''}业务订单`);

  // 从创建页返回时的成功提示：/orders?service=xxx&created=1
  const createSuccessShownRef = useRef(false);
  useEffect(() => {
    const created = searchParams.get('created');
    if (created === '1' && !createSuccessShownRef.current) {
      createSuccessShownRef.current = true;
      message.success('订单添加成功');
      const params = new URLSearchParams(searchParams.toString());
      params.delete('created');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // 加载客户等级和来源选项
  useEffect(() => {
    const loadOptions = async () => {
      try {
        // 加载客户等级
        const customerTypes = await optionService.get('customer_types', []);
        if (Array.isArray(customerTypes)) {
          setClientTypeOptions(customerTypes.map(type => ({
            value: type,
            label: type
          })));
        }

        // 加载客户来源
        const customerSources = await optionService.get('customer_sources', []);
        if (Array.isArray(customerSources)) {
          setSourceOptions(customerSources.map(source => ({
            value: source,
            label: source
          })));
        }
      } catch (error) {
        console.error('Failed to load options:', error);
      }
    };
    loadOptions();
  }, []);

  // 用于跟踪是否已经初始化
  const isInitialized = useRef(false);

  const { hasRole, state } = useAuth();
  const isAdminUser = hasRole('admin');
  const isManagerUser = hasRole('sales_manager') || hasRole('coordinator_manager');
  const currentUserId = state.user?.id;
  const permissions = usePermissions();
  const canCreate = permissions.hasActionAccess('order-management', 'create');
  const canEdit = permissions.hasActionAccess('order-management', 'edit');
  const canDelete = permissions.hasActionAccess('order-management', 'delete');
  const canImport = permissions.hasActionAccess('order-management', 'import');
  const canExport = permissions.hasActionAccess('order-management', 'export');
  const canCopy = canCreate && permissions.hasActionAccess('order-management', 'copy');
  const canViewDetail = permissions.hasActionAccess('order-management', 'view_details');
  const canUpdateStatus = permissions.hasActionAccess('order-management', 'update_status');
  const canMonitor = permissions.hasActionAccess('order-management', 'monitor');
  const hasCurrentServiceAccess = !serviceFilter || permissions.hasOrderServiceAccess(serviceFilter);

  // 状态选项
  const statusOptions = [
    { value: 'pending', label: '待处理', color: 'orange' },
    { value: 'processing', label: '处理中', color: 'blue' },
    { value: 'completed', label: '已完成', color: 'green' },
    { value: 'cancelled', label: '已取消', color: 'red' },
  ];

  const searchFieldOptions = useMemo(() => ([
    { value: 'all', label: '全部字段' },
    { value: 'order_no', label: '订单编号' },
    { value: 'case_no', label: '案件号' },
    { value: 'applicant_name', label: '申请人名称' },
    { value: 'contact_name', label: '客户名称' },
    { value: 'client_email', label: '客户邮箱' },
    { value: 'trademark_name', label: '商标名称' },
    { value: 'application_no', label: '申请号' },
    { value: 'registration_no', label: '注册号' },
    { value: 'patent_name', label: '专利名称' },
    { value: 'work_name', label: '作品名称' },
    { value: 'company_name', label: '公司名称' },
  ]), []);

  // 业务类型选项（保持与SERVICE_TYPES一致）
  const serviceTypeOptions = SERVICE_TYPES.map(service => ({
    value: service.value,
    label: service.label.replace('服务', '')
  }));

  /**
   * 跳转到订单详情页面。
   */
  const handleViewDetail = (orderId?: number) => {
    if (!orderId) {
      message.warning('无法打开缺少编号的订单详情');
      return;
    }
    navigate(`/orders/${orderId}`);
  };

  // 筛选变化与重置逻辑由 useOrderFilters 提供

  // 获取筛选选项
  const fetchFilterOptions = async () => {
    try {
      const params = serviceFilter ? { service: serviceFilter } : {};
      const response = await orderService.getFilterOptions(params);
      if (response.success) {
        setFilterOptions(prev => ({
          ...response.data
        }));
        setSellerOptions(response.data.sellers || []);
      }
    } catch (error) {
      console.error('获取筛选选项失败:', error);
    }
  };

  // 获取当前业务类型的业务名称选项
  const getServiceNameOptions = () => {
    if (!serviceFilter || serviceFilter === 'madrid') return [];
    const serviceTypeMap = SERVICE_TYPE_MAP[serviceFilter] || {};
    const serviceNameOptions = Object.entries(serviceTypeMap).map(([key, value]) => ({
      value: key,
      label: value
    }));
    return serviceNameOptions;
  };

  // 检查商标监控状态
  // 商标监控状态检查已移至后端，现在直接从订单数据的 is_monitored 字段获取

  // 订单数据
  const { orders: orders, setOrders, loading, fetchOrders } = useOrders({
    searchText,
    searchField,
    serviceFilter,
    subServiceFilter,
    filters,
    pagination,
    setPagination,
    sortConfig,
  } as any);

  // 联动效果：筛选/排序/分页变化触发数据拉取或重置页码
  useOrdersEffects({
    isInitialized,
    pagination: pagination as any,
    setPagination: setPagination as any,
    updateUrlParams,
    fetchOrders,
    searchText,
    searchField,
    serviceFilter,
    subServiceFilter,
    filters,
    sortConfig: sortConfig as any,
  });

  // 表格选择与批量操作
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    // 仅允许选择父单或子单都可以，根据当前数据结构通用处理
    preserveSelectedRowKeys: true,
  }), [selectedRowKeys]);

  const handleBulkDelete = useCallback(async () => {
    if (!selectedRowKeys.length) return;
    const ids = selectedRowKeys as number[];
    try {
      const response = await orderService.batchDeleteOrders(ids);
      if (response.success) {
        message.success(response.message || `成功删除 ${response.data?.deleted_count || ids.length} 个订单`);
        setSelectedRowKeys([]);
        fetchOrders();
      } else {
        message.error(response.message || '批量删除失败');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || '批量删除失败');
    }
  }, [selectedRowKeys, fetchOrders]);

  // 批量编辑订单
  const handleBatchEdit = async () => {
    try {
      const values = await batchEditForm.validateFields();

      // 只提交有值的字段
      const updateData: any = {};
      Object.keys(values).forEach(key => {
        const value = values[key];
        if (value !== undefined && value !== null && value !== '') {
          // 如果是日期对象 (dayjs)，格式化为字符串
          if (dayjs.isDayjs(value)) {
             updateData[key] = value.format('YYYY-MM-DD');
          } else {
             updateData[key] = value;
          }
        }
      });

      if (Object.keys(updateData).length === 0) {
        message.warning('请至少填写一个要修改的字段');
        return;
      }

      const res = await orderService.batchUpdateOrders({
        ids: selectedRowKeys as number[],
        data: updateData
      });

      if (res.success) {
        message.success(`成功更新 ${selectedRowKeys.length} 个订单`);
        setBatchEditModalVisible(false);
        batchEditForm.resetFields();
        setSelectedRowKeys([]);
        fetchOrders(); // 刷新列表
      } else {
        message.error(res.message || '批量更新失败');
      }
    } catch (error: any) {
      console.error('批量编辑失败:', error);
      if (error.errorFields) {
        // 表单验证错误,不显示消息
        return;
      }
      message.error('批量编辑失败');
    }
  };

  // 父子展开：缓存每个父单的子订单
  const [childrenMap, setChildrenMap] = useState<Record<number, Order[]>>({});
  const fetchChildren = useCallback(async (parentId: number) => {
    try {
      const res = await orderService.getOrders({ parent_id: parentId, per_page: 1000 });
      if (res.success) {
        setChildrenMap(prev => ({ ...prev, [parentId]: res.data.data }));
      }
    } catch (e) {
      console.warn('加载子订单失败', e);
    }
  }, []);

  // 更新状态弹窗
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [statusForm] = Form.useForm();
  const [statusOptionsForModal, setStatusOptionsForModal] = useState<Array<{ value: string; label: string; disabled?: boolean }>>([]);
  const [nextStatusOptions, setNextStatusOptions] = useState<Array<{ value: string; label: string }>>([]);
  const currentStatusOrderRef = useRef<Order | null>(null);
  const [statusHistory, setStatusHistory] = useState<import('../services/order').OrderStatus[]>([]);
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const watchedSubService = Form.useWatch('sub_service', form);
  const batchWatchedSubService = Form.useWatch('sub_service', batchForm);
  const { formattedMap: statusOptionsMap } = useOrderStatusOptions();

  const buildStatusOptions = (order: Order | null, history: import('../services/order').OrderStatus[]) => {
    const ext = order && order.service ? (order[order.service as keyof Order] as any) : undefined;
    const subService = ext?.sub_service;
    const fullOptions = getStatusOptionsBySubService(order?.service || 'trademark', subService, statusOptionsMap);
    const chosen = new Set((history || []).map(s => s.status).filter(Boolean));
    return fullOptions.map(opt => ({ ...opt, disabled: chosen.has(opt.value) }));
  };

  // 下个状态：独立配置（默认/按服务）
  const buildNextStatusOptions = (order: Order | null) => {
    const service = order?.service || '';
    const ext = order && service ? (order[service as keyof Order] as any) : undefined;
    const sub_service = ext?.sub_service;

    // 默认
    let opts: Array<{ value: string; label: string }> = [
      { value: '待宣誓', label: '待宣誓' },
      { value: '待续展', label: '待续展' },
    ];

    if (service === 'company' && (sub_service === 'registration' || sub_service === 'annual_review' || sub_service === 'audit')) {
      opts = [
        { value: '待年审', label: '待年审' },
        { value: '其他', label: '其他' },
      ];
    } else if (service === 'copyright' || service === 'barcode') {
      opts = [
        { value: '待续费', label: '待续费' },
        { value: '其他', label: '其他' },
      ];
    } else if (service === 'patent') {
      opts = [
        { value: '待交年费', label: '待交年费' },
        { value: '其他', label: '其他' },
      ];
    }

    return opts;
  };

  const refreshStatusHistory = async (shouldResetForm: boolean) => {
    const order = currentStatusOrderRef.current;
    if (!order?.id) {
      setStatusHistory([]);
      setStatusOptionsForModal([]);
      return;
    }
    try {
      const res = await orderService.getStatusHistory(order.id);
      const history = res.success ? ((res.data || []).slice()) : [];
      setStatusHistory(history);
      const options = buildStatusOptions(order, history);
      setStatusOptionsForModal(options);
      setNextStatusOptions(buildNextStatusOptions(order));
      if (shouldResetForm) {
        const firstAvailable = options.find(o => !o.disabled)?.value;
        const latestWithDates = [...history].reverse().find(s => s.due_at || (s as any).expiration_at);
        const fallbackDue = (order as any)?.due_at || (latestWithDates as any)?.due_at;
        const fallbackExpiration = (order as any)?.expiration_at || (latestWithDates as any)?.expiration_at;
        const fallbackNext = (latestWithDates as any)?.next_status || (order as any)?.next_status;
        statusForm.setFieldsValue({
          status: undefined,
          created_at: dayjs(),
          remark: '',
          next_status: fallbackNext,
          due_at: fallbackDue ? dayjs(fallbackDue) : undefined,
          expiration_at: fallbackExpiration ? dayjs(fallbackExpiration) : undefined,
          files: [],
        } as any);
      }
    } catch (e) {
      setStatusHistory([]);
      setStatusOptionsForModal(buildStatusOptions(order || null, []));
    }
  };

  const handleDeleteStatus = async (statusId: number) => {
    const order = currentStatusOrderRef.current;
    if (!order?.id) return;
    const res = await orderService.deleteStatus(order.id, statusId);
    if (res.success) {
      message.success('删除状态记录成功');
      await refreshStatusHistory(false);
    } else {
      message.error(res.message || '删除状态记录失败');
    }
  };

  const openUpdateStatusModal = async (order: Order) => {
    currentStatusOrderRef.current = order;
    await refreshStatusHistory(true);
    setStatusModalVisible(true);
  };
  // 获取最新状态，用于动态更新进度条（避免在未挂载时使用 useWatch 触发警告）
  const getWatchedStatus = () => currentStatusOrderRef.current?.status || statusForm.getFieldValue('status');
  // 根据历史记录构建状态->完成时间映射
  const statusFinishTimeMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    (statusHistory || []).forEach((s) => {
      const key = s.status || '';
      if (!key) return;
      const ts = s.created_at || '';
      if (!ts) return;
      // 若同名状态出现多次，保留最后一次；仅显示日期
      map[key] = dayjs(ts).isValid() ? dayjs(ts).format('YYYY-MM-DD HH:mm') : ts;
    });
    return map;
  }, [statusHistory]);
  const handleSaveStatus = async () => {
    try {
      const values = await statusForm.validateFields();
      const order = currentStatusOrderRef.current;
      if (!order?.id) return;
      const filesValue = values.files;
      const files = typeof filesValue === 'string'
        ? filesValue.split(',').map((v: string) => parseInt(v.trim(), 10)).filter((n: number) => !isNaN(n))
        : Array.isArray(filesValue) ? filesValue : [];
      const res = await orderService.addStatus(order.id, values.status, {
        updateOrderStatus: true,
        remark: values.remark,
        created_at: values.created_at ? dayjs(values.created_at).format('YYYY-MM-DD HH:mm:ss') : undefined,
        next_status: values.next_status,
        due_at: values.due_at ? dayjs(values.due_at).format('YYYY-MM-DD') : null,
        expiration_at: values.expiration_at ? dayjs(values.expiration_at).format('YYYY-MM-DD') : null,
        files: files || [],
        application_no: values.application_no,
        registration_no: values.registration_no,
      });
      if (res.success) {
        message.success('状态已更新');
        setStatusModalVisible(false);
        statusForm.resetFields();
        fetchOrders();
      } else {
        message.error(res.message || '更新失败');
      }
    } catch (e) {}
  };

  useEffect(() => {
    fetchFilterOptions();
  }, [serviceFilter]); // 当业务类型改变时重新获取筛选选项

  // 加载子业务选项：直接从 SERVICE_TYPE_MAP 获取当前 service 的子业务
  useEffect(() => {
    const loadSubServices = async () => {
      if (!serviceFilter) {
        setSubServiceOptions([]);
        setSubServiceFilter('');
        return;
      }
      // 马德里商标没有子业务类型
      if (serviceFilter === 'madrid') {
        setSubServiceOptions([]);
        setSubServiceFilter('');
        return;
      }
      const map = (SERVICE_TYPE_MAP as any)[serviceFilter] || {};
      const options = Object.entries(map).map(([value, label]: any) => ({ value, label }));
      setSubServiceOptions(options as any);
      // 若 URL 指定了 sub_service，且存在于当前服务的子业务列表，则使用它；否则重置为“全部”
      const urlSub = searchParams.get('sub_service') || '';
      const exists = (options as any).some((opt: any) => opt.value === urlSub);
      setSubServiceFilter(exists ? urlSub : '');
    };
    loadSubServices();
  }, [serviceFilter]);

  // 处理复制订单
  const handleCopy = (order: Order) => {
    // 2. 提取当前具体业务类的扩展数据并展平，这与 handleEdit 的逻辑相同
    const svc = order.service || 'trademark';
    const ext: any = (order as any)[svc] || {};

    const copiedOrder = {
      ...order,
      ...ext,
      id: undefined,
      order_no: generateOrderNo(),
      application_no: '',
    } as any;

    setEditingOrder(null);
    const formData = {
      ...copiedOrder,
      due_at: copiedOrder.due_at ? dayjs(copiedOrder.due_at) : null,
      payment_at: copiedOrder.payment_at ? dayjs(copiedOrder.payment_at) : null,
      created_at: copiedOrder.created_at ? dayjs(copiedOrder.created_at) : null,
    };

    // 与 handleEdit 保持一致，对必要的日期字段进行格式化
    ['opened_at','a1_first_use_at','a1_first_commercial_use_at','b44_registration_at','d44_application_at','sold_at']
      .forEach((k) => { if (formData[k]) formData[k] = dayjs(formData[k]); });

    form.setFieldsValue(formData);
    setSelectedServiceType(svc);
    
    // 清除由于马德里父单可能带出的子单国家关联
    setBatchSelectedNations([]);

    setModalVisible(true);

    message.success('已复制订单信息，请填写申请号后保存');
  };

  // 处理新增/编辑
  const handleEdit = (order?: Order) => {
    if (order?.id) {
      // 打开编辑弹窗并填充表单
      const svc = order.service || 'trademark';
      setSelectedServiceType(svc);
      setEditingOrder(order);
      const ext: any = (order as any)[svc] || {};
      const formData: any = {
        ...order,
        ...ext,
        due_at: order.due_at ? dayjs(order.due_at) : null,
        payment_at: order.payment_at ? dayjs(order.payment_at) : null,
        created_at: order.created_at ? dayjs(order.created_at) : null,
      };
      ['opened_at','a1_first_use_at','a1_first_commercial_use_at','b44_registration_at','d44_application_at','sold_at']
        .forEach((k) => { if (formData[k]) formData[k] = dayjs(formData[k]); });
      form.setFieldsValue(formData);
      setModalVisible(true);
      // 如果为马德里父单，预填“指定国家”为其子订单的国家
      if (order.order_type === 'madrid' && order.id) {
        orderService.getOrders({ parent_id: order.id, per_page: 1000 } as any)
          .then((res) => {
            if (res?.success && Array.isArray(res.data?.data)) {
              const nations = (res.data.data as any[])
                .map((o: any) => o?.nation)
                .filter((n: any) => typeof n === 'string' && n.trim().length > 0);
              setBatchSelectedNations(Array.from(new Set(nations)) as any);
            } else {
              setBatchSelectedNations([]);
            }
          })
          .catch(() => setBatchSelectedNations([]));
      } else {
        setBatchSelectedNations([]);
      }
      return;
    }
    // 无记录时视为新增：直接按当前服务打开新增弹窗
    const svc = serviceFilter || 'trademark';
    handleServiceSelect(svc);
  };

  // 处理删除
  const handleDelete = async (id: number) => {
    if (!id) {
      message.error('订单ID无效');
      return;
    }
    try {
      const response = await orderService.deleteOrder(id);
      if (response.success) {
        message.success('订单删除成功');
        fetchOrders();
      } else {
        message.error(getApiResponseErrorMessage(response, '删除失败'));
      }
    } catch (error: any) {
      console.error('Delete order error:', error);
      message.error(getAxiosErrorMessage(error, '删除失败'));
    }
  };

  // 添加到商标监控
  const handleAddToMonitor = async (order: Order, applicationNo?: string) => {
    const appNo = applicationNo || (order.trademark as any)?.application_no;
    if (!appNo) {
      message.error('申请号不能为空');
      return;
    }
    try {
      // 组装通知邮箱：优先使用客户用户的 email 与 users.notification_emails；无 client_id 时回退到订单的 client_email
      let notifyEmails: string[] = [];
      try {
        if (order.client_id) {
          const res = await userService.getUser(Number(order.client_id));
          if (res.success && res.data) {
            if (res.data.email) notifyEmails.push(res.data.email);
            const notificationEmailsField = (res.data as any).notification_emails;
            if (notificationEmailsField) notifyEmails.push(notificationEmailsField);
          }
        }
      } catch (e) {
        // 忽略用户信息拉取失败，使用订单邮箱回退
      }

      if ((!notifyEmails.length) && order.client_email) {
        notifyEmails.push(order.client_email);
      }
      // 规范化：分号拆分、去空格、去重
      const normalized = Array.from(new Set(notifyEmails
        .flatMap(s => String(s).split(';'))
        .map(s => s.trim())
        .filter(Boolean)));

      const response = await trademarkService.addFromOrder({
        application_no: appNo,
        user_email: normalized.join('; '),
        nation: order.nation || 'CN',
        client_id: order.client_id ? Number(order.client_id) : undefined,
      });
      if (response.success) {
        message.success('已添加到商标监控');
        setOrders(prevOrders => prevOrders.map(o => {
          const orderAppNo = (o.trademark as any)?.application_no;
          return orderAppNo === appNo ? { ...o, is_monitored: true } : o;
        }));
      } else {
        message.error(getApiResponseErrorMessage(response, '添加失败'));
      }
    } catch (error: any) {
      console.error('添加到监控失败:', error);
      message.error(getAxiosErrorMessage(error, '添加失败'));
    }
  };

  // 获取所有可用的列定义
  const getAllColumnDefinitions = (serviceType: string, isFiltered: boolean = false) => {
    const allColumnDefinitions: Record<string, any> = buildAllColumnDefinitions({
      serviceType,
      SERVICE_TYPES,
      handleEdit,
      handleDelete,
      handleCopy,
      handleViewDetail,
      handleAddToMonitor,
      openUpdateStatusModal,
      canEdit,
      canDelete,
      canCopy,
      canViewDetail,
      canUpdateStatus,
      canMonitor,
      filterOptions,
      statusOptions,
    });

    const serviceSpecificFields: Record<string, string[] | Record<string, string[]>> = SERVICE_COLUMNS_MAP as any;

    let fields: any = (serviceSpecificFields as any)[serviceType] || (serviceSpecificFields as any).trademark;
    // 当为对象映射（按子业务划分）时，根据当前子业务选择列集合；否则直接使用数组
    if (!Array.isArray(fields)) {
      const map = fields as Record<string, string[]>;
      const selector = subServiceFilter;

      fields = selector && map[selector] ? map[selector] : map._default;
    }

    // 构建默认显示的列
    let defaultColumns = [...fields];

    defaultColumns.push('actions');

    return { allColumnDefinitions, defaultColumns };
  };

  // 根据业务类型生成列配置
  const getColumnsForService = useCallback((serviceType: string, isFiltered: boolean = false): ColumnsType<Order> => {
    const { allColumnDefinitions, defaultColumns } = getAllColumnDefinitions(serviceType, isFiltered);

    // 如果没有设置可见列，使用默认列
    const columnsToShow = (Array.isArray(visibleColumns) && visibleColumns.length > 0) ? visibleColumns : defaultColumns;

    // 确保 columnsToShow 是数组
    if (!Array.isArray(columnsToShow)) {
      return [];
    }

    // 先构造列对象集合，再按“列显示权限”过滤
    const candidateColumns = columnsToShow
      .map((columnKey: string) => ({ key: columnKey, permissionService: serviceType, ...allColumnDefinitions[columnKey] }))
      .filter((col: any) => !!col && !!col.key);

    const permitted = permissions.getVisibleColumns('order-management', candidateColumns as any);

    return permitted.map((c: any) => ({ ...c }));
  }, [visibleColumns, getAllColumnDefinitions]);

  // 马德里子单需要隐藏的列
  const madridChildHiddenColumns = useMemo(() => new Set<string>([
    'payment_at', 'price', 'cost', 'group_number', 'commission', 'seller', 'channel', 'contact_name', 'remark'
  ]), []);

  // 根据当前业务类型生成列（基础版，不包含马德里子单裁剪）
  const baseColumns = useMemo(() => {
    return getColumnsForService(serviceFilter || 'all', !!serviceFilter);
  }, [serviceFilter, subServiceFilter, getColumnsForService]);

  // 是否仅展示马德里子单列表
  const showingOnlyMadridChild = useMemo(() => {
    return orders.length > 0 && orders.every(o => o.order_type === 'madrid_child');
  }, [orders]);

  // 主表列：若是纯马德里子单列表，则裁剪掉财务/渠道列
  const columns = useMemo(() => {
    const prune = (cols: ColumnsType<Order>) => cols.filter((col: any) => !madridChildHiddenColumns.has(col.key as string));
    return showingOnlyMadridChild ? prune(baseColumns) : baseColumns;
  }, [baseColumns, madridChildHiddenColumns, showingOnlyMadridChild]);

  // 子表（展开的马德里子单列表）固定使用裁剪后的列
  const childColumns = useMemo(() => {
    return baseColumns.filter((col: any) => !madridChildHiddenColumns.has(col.key as string));
  }, [baseColumns, madridChildHiddenColumns]);

  // 初始化可见列 - 当业务类型改变时加载用户保存的列设置或使用默认设置
  useEffect(() => {
    const loadColumnSettings = async () => {
      if (serviceFilter) {
        try {
          // 尝试从用户偏好中加载列设置
          const savedColumns = await userPreferenceService.getColumnSettings('orders', serviceFilter, subServiceFilter || undefined);

            if (savedColumns && savedColumns.length > 0) {
            let sanitized = savedColumns;
            // 全局移除 ID 列
            sanitized = sanitized.filter((c: string) => c !== 'id');
            // 若限定 sub_service，过滤掉不属于该 sub_service 的列
            if (subServiceFilter) {
              const map = (SERVICE_COLUMNS_MAP as any)[serviceFilter] || {};
              const allowed = new Set<string>();
              if (!Array.isArray(map)) {
                ((map[subServiceFilter] || map._default || []) as string[]).forEach(k => allowed.add(k));
              }
              ['actions'].forEach(k => allowed.add(k));

              sanitized = sanitized.filter((c: string) => allowed.has(c));
              // 确保 actions 存在且在末尾
              sanitized = sanitized.filter((c: string) => c !== 'actions');
              sanitized.push('actions');
            }
            setVisibleColumns(sanitized);
          } else {
            // 使用默认设置
            const { defaultColumns } = getAllColumnDefinitions(serviceFilter, true);
            const reorderedColumns = defaultColumns.filter(col => col !== 'actions');
            reorderedColumns.push('actions');
            setVisibleColumns(reorderedColumns);
          }
        } catch (error) {
          console.warn('加载列设置失败，使用默认设置:', error);
          // 出错时使用默认设置
          const { defaultColumns } = getAllColumnDefinitions(serviceFilter, true);
          const reorderedColumns = defaultColumns.filter(col => col !== 'actions');
          reorderedColumns.push('actions');
          setVisibleColumns(reorderedColumns);
        }
      }
    };

    loadColumnSettings();
  }, [serviceFilter, subServiceFilter]);

  // 切换子业务时：按 service+sub_service 维度加载用户设置；
  // 如无保存，则回退至该子业务默认列集合（由上方 loadColumnSettings 实现）

  // 同步分页状态到 URL 由 useOrderPagination 提供 updateUrlParams

  // 加载每页条数设置
  useEffect(() => {
    const loadPageSizeSettings = async () => {
      try {
        const savedPageSize = await userPreferenceService.getPageSizeSettings('orders', serviceFilter || undefined);
        // 优先使用 URL 参数，如果没有则使用保存的偏好
        const urlPageSize = parseInt(searchParams.get('pageSize') || '0', 10);
        const finalPageSize = urlPageSize > 0 ? urlPageSize : savedPageSize;

        // 只更新 pageSize，不更新 current，避免覆盖 URL 中的页码
        if (pagination.pageSize !== finalPageSize) {
          setPagination(prev => ({
            ...prev,
            pageSize: finalPageSize
          }));
          // 同步 pageSize 到 URL（保持当前页码不变）
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('pageSize', finalPageSize.toString());
          setSearchParams(newSearchParams, { replace: true });
        }
      } catch (error) {
        console.error('加载每页条数设置失败:', error);
      }
    };

    loadPageSizeSettings();
    // 延迟标记初始化完成并获取数据
    setTimeout(() => {
      isInitialized.current = true;
      fetchOrders();
    }, 0);
  }, [serviceFilter]);

  // 获取当前业务类型的所有可用列
  const getAvailableColumns = () => {
    const targetService = serviceFilter || 'all';
    const { allColumnDefinitions } = getAllColumnDefinitions(targetService, true);

    const serviceColumns = new Set<string>();
    const config = (SERVICE_COLUMNS_MAP as any)[targetService];

    if (targetService === 'all') {
      getServiceColumnsUnion('all').forEach((k: string) => serviceColumns.add(k));
    } else if (Array.isArray(config)) {
      config.forEach((k: string) => serviceColumns.add(k));
    } else if (config && typeof config === 'object') {
      if (subServiceFilter && (config as any)[subServiceFilter]) {
        ((config as any)[subServiceFilter] as string[]).forEach((k: string) => serviceColumns.add(k));
      } else if ((config as any)._default) {
        ((config as any)._default as string[]).forEach((k: string) => serviceColumns.add(k));
      } else {
        Object.values(config).forEach((arr: any) => {
          if (Array.isArray(arr)) arr.forEach((k: string) => serviceColumns.add(k));
        });
      }
    } else {
      const unionKeys = getServiceColumnsUnion(targetService);
      unionKeys.forEach((k: string) => serviceColumns.add(k));
    }

    ['actions'].forEach(k => serviceColumns.add(k));

    const all = Array.from(serviceColumns)
      .filter(key => allColumnDefinitions[key])
      .map(key => ({ key, title: allColumnDefinitions[key].title, fixed: allColumnDefinitions[key].fixed, permissionService: targetService }));

    const permitted = permissions.getVisibleColumns('order-management', all as any);

    return permitted;
  };

  // 处理列显示变更
  const handleColumnChange = async (checkedColumns: string[]) => {
    // 若限定了 sub_service，则隐藏不属于该 sub_service 的列
    let allowed = new Set<string>();
    if (serviceFilter && subServiceFilter) {
      const map = (SERVICE_COLUMNS_MAP as any)[serviceFilter] || {};
      if (!Array.isArray(map)) {
        ((map[subServiceFilter] || map._default || []) as string[]).forEach(k => allowed.add(k));
      }
      ['actions'].forEach(k => allowed.add(k));
    }

    // 保留用户拖拽设置的顺序，只过滤不允许的列
    // 注意：不再重新按默认顺序排序，以支持用户自定义列顺序
    const filteredColumns = checkedColumns
      .filter(col => col !== 'actions')
      .filter(col => allowed.size === 0 || allowed.has(col));

    const uniqueColumns = [...filteredColumns, 'actions'];

    setVisibleColumns(uniqueColumns);

    if (serviceFilter) {
      try {
        await userPreferenceService.saveColumnSettings('orders', uniqueColumns, serviceFilter, subServiceFilter || undefined);
      } catch (error) {
        console.error('保存列设置失败:', error);
      }
    }
  };

  // 渲染列选择器
  const renderColumnSelector = () => {
    // const available = getAvailableColumns().filter(col => !(serviceFilter === 'other' && col.key === 'sub_service'));
    const available = getAvailableColumns();
    // const visible = (Array.isArray(visibleColumns) ? visibleColumns : []).filter(col => !(serviceFilter === 'other' && col === 'sub_service'));
    const visible = (Array.isArray(visibleColumns) ? visibleColumns : []);
    return (
      <ColumnSelector
        availableColumns={available}
        visibleColumns={visible}
        onColumnChange={handleColumnChange}
        fixedColumns={['actions']}
      />
    );
  };


  // 处理业务类型选择
  const handleServiceSelect = (serviceType: string) => {
    // 打开前清空表单，避免上一次的数据残留（包括 client_id 等会触发只读的字段）
    form.resetFields();
    form.setFieldsValue({
      client_id: undefined,
      contact_name: undefined,
      client_email: undefined,
      notification_emails: undefined,
      phone: undefined,
      group_number: undefined,
      client_type: undefined,
      channel: undefined,
      source: undefined,
      files: undefined,
      // 服务切换时显式清空子业务，避免沿用上一次服务的子业务
      sub_service: undefined,
    });
    setSelectedServiceType(serviceType);
    setEditingOrder(null);
    setModalVisible(true);

    // 根据当前列表的 sub_service（URL）和业务类型，预选子业务
    try {
      const urlSub = subServiceFilter;
      if (urlSub) {
        const map = (SERVICE_TYPE_MAP as any)[serviceType] || {};
        if (Object.prototype.hasOwnProperty.call(map, urlSub)) {
          form.setFieldsValue({ sub_service: urlSub });
        }
      } else if (serviceType === 'other') {
        // 其它服务默认 registration
        form.setFieldsValue({ sub_service: 'registration' });
      }
    } catch (e) {
      // 忽略预选失败
    }
  };

  // 当列表子业务切换且“新增订单”弹窗已打开（且非编辑模式）时，实时同步默认子业务
  useEffect(() => {
    if (modalVisible && !editingOrder && selectedServiceType) {
      try {
        const map = (SERVICE_TYPE_MAP as any)[selectedServiceType] || {};
        if (subServiceFilter && Object.prototype.hasOwnProperty.call(map, subServiceFilter)) {
          form.setFieldsValue({ sub_service: subServiceFilter });
        }
      } catch (e) {}
    }
  }, [subServiceFilter, modalVisible, editingOrder, selectedServiceType]);

  // 处理复制订单、编辑 已提前上移（避免初始化前被引用）
  // 原地删除重复定义

  // 处理用户创建（如果用户不存在）
  const handleUserCreation = async (values: any) => {
    const contactName = values.contact_name?.trim();
    const userEmail = values.client_email?.trim();
    const clientId = values.client_id;

    // 如果没有 client_id，说明没有找到匹配的用户，需要创建新用户
    if (!clientId && contactName && userEmail) {
      try {
        // 上级用户逻辑：优先取订单表单中选择的 seller；否则回退为当前登录用户
        const parentId = (typeof values.seller !== 'undefined' && values.seller !== null && values.seller !== '')
          ? Number(values.seller)
          : (currentUserId as number | undefined);

        const newUserData = {
          username: contactName,
          display_name: contactName,
          email: userEmail,
          phone: values.phone,
          client_type: values.client_type,
          customer_categories: values.customer_categories,
          contact_name: contactName,
          group_number: values.group_number,
          channel: values.channel,
          source: values.source,
          notification_emails: values.notification_emails,
          parent_id: parentId ?? null,
          client_role: values.client_role, // Pass the selected role
          password: Math.random().toString(36).substr(2, 12) // 生成临时密码
        };

        const response = await api.post('/users', newUserData);

        if (response.data && response.data.success) {
          const newUser = response.data.data;
          // 更新表单的 client_id
          form.setFieldsValue({
            client_id: newUser.id
          });
          console.log('已自动创建新用户:', newUser.display_name || newUser.username);
          return newUser.id;
        } else {
          console.log('用户创建响应失败:', response.data);
        }
      } catch (error) {
        console.error('自动创建用户失败:', error);
        // 不阻止订单创建，只是记录错误
      }
    } else {
      console.log('跳过用户创建 - 条件不满足');
    }
    return clientId;
  };

  // 处理保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      console.log('提交的表单值：', values);

      // 处理用户创建逻辑
      const finalClientId = await handleUserCreation(values);

      // 获取最新的表单值（可能包含新创建的 client_id）
      const updatedValues = form.getFieldsValue();

      // 计算提成和利润
      const calculationResult = await CommissionCalculator.calculate({
        price: values.price,
        cost: values.cost,
        client_type: values.client_type
      });

      // 如果有错误但不是严重错误，显示警告但继续保存
      if (calculationResult.hasError && calculationResult.errorMessage) {
        console.warn('Commission calculation warning:', calculationResult.errorMessage);
        // 可以选择在这里显示警告消息
        // message.warning(calculationResult.errorMessage);
      }

      const formatDate = (d?: any) => (d && typeof d.format === 'function') ? d.format('YYYY-MM-DD') : d;

      const orderData = {
        ...updatedValues,
        client_id: finalClientId || updatedValues.client_id, // 确保使用最新的 client_id
        service: selectedServiceType, // 服务主类别 (trademark, patent, etc.)
        // sub_service 来自表单values，是具体的业务类型，将保存到扩展表中
        profit: calculationResult.profit.toString(),
        commission: calculationResult.commission.toString(),
        // 主表日期字段
        due_at: updatedValues.due_at?.format('YYYY-MM-DD HH:mm:ss'),
        payment_at: updatedValues.payment_at?.format('YYYY-MM-DD HH:mm:ss'),
        created_at: updatedValues.created_at?.format('YYYY-MM-DD HH:mm:ss'),
        // 扩展表日期字段（按需存在）
        opened_at: formatDate(updatedValues.opened_at),
        a1_first_use_at: formatDate(updatedValues.a1_first_use_at),
        a1_first_commercial_use_at: formatDate(updatedValues.a1_first_commercial_use_at),
        b44_registration_at: formatDate(updatedValues.b44_registration_at),
        d44_application_at: formatDate(updatedValues.d44_application_at),
        sold_at: formatDate(updatedValues.sold_at),
      };

      // 商标类别多选：将数组转换为以逗号分隔的字符串，避免后端列类型兼容问题
      if (selectedServiceType === 'trademark') {
        const pc = updatedValues.primary_class;
        if (Array.isArray(pc)) {
          (orderData as any).primary_class = pc.join(',');
        }
      }

      let response;
      if (editingOrder && editingOrder.id) {
        response = await orderService.updateOrder(editingOrder.id, orderData);
      } else {
        response = await orderService.createOrder(orderData);
      }

      if (response.success) {
        // 若编辑的是马德里父单：为"新增的指定国家"创建子订单
        if (editingOrder && editingOrder.order_type === 'madrid' && Array.isArray(batchSelectedNations)) {
          try {
            const childrenRes = await orderService.getOrders({ parent_id: editingOrder.id as number, per_page: 1000 } as any);
            const children: any[] = (childrenRes?.success ? (childrenRes.data?.data || []) : []) as any[];
            // 现有子单：nation -> ids
            const existingMap = new Map<string, number[]>();
            for (const c of children) {
              const nat = typeof c?.nation === 'string' ? c.nation : '';
              if (!nat) continue;
              if (!existingMap.has(nat)) existingMap.set(nat, []);
              if (c?.id) existingMap.get(nat)!.push(c.id as number);
            }
            const existingNations = new Set<string>(Array.from(existingMap.keys()));
            const selectedSet = new Set<string>(batchSelectedNations.filter((n) => typeof n === 'string' && n));

            // 获取父订单的 sub_service
            const parentSubService = (orderData as any).sub_service || 'registration';

            // 需要新增 = 选中的 - 已有的
            const toCreate = Array.from(selectedSet).filter((n) => !existingNations.has(n));
            if (toCreate.length > 0) {
              const baseData: any = {
                ...orderData,
                id: undefined,
                parent_id: editingOrder.id,
                service: 'trademark',
                sub_service: 'registration',
                order_type: 'madrid_child',
                registration_no: (updatedValues as any)?.international_registration_no,
              };
              // 使用父订单的业务类型获取订单编号前缀
              const orderNoPrefix = getOrderPrefix(editingOrder.service || 'trademark', (orderData as any).sub_service || 'registration');
              // 对每个国家创建子单
              await Promise.allSettled(toCreate.map((nation) => {
                const payload = {
                  ...baseData,
                  nation,
                  order_no: generateOrderNoWithPrefix(orderNoPrefix),
                } as any;
                return orderService.createOrder(payload);
              }));
            }

            // 需要删除 = 已有的 - 选中的
            const toDeleteNations = Array.from(existingNations).filter((n) => !selectedSet.has(n));
            if (toDeleteNations.length > 0) {
              const toDeleteIds: number[] = toDeleteNations.flatMap((n) => existingMap.get(n) || []);
              if (toDeleteIds.length > 0) {
                await Promise.allSettled(toDeleteIds.map((id) => orderService.deleteOrder(id)));
              }
            }
          } catch (e) {
            // 忽略子单创建失败的细节，主单已成功
            console.warn('创建马德里子单失败', e);
          }
        }

        message.success(editingOrder ? '订单更新成功' : '订单添加成功');
        setModalVisible(false);
        fetchOrders();
      } else {
        message.error(getApiResponseErrorMessage(response, '保存失败'));
      }
    } catch (error: any) {
      console.error('Save order error:', error);
      message.error(getAxiosErrorMessage(error, '保存失败'));
    }
  };

  // 处理取消
  const handleCancel = () => {
    setModalVisible(false);
    setSelectedServiceType('');
    form.resetFields();
  };

  // 处理导出
  const handleExport = async () => {
    try {
      // 获取当前所有筛选条件（与列表保持一致）
      const params = {
        search: searchText,
        search_field: searchField,
        service: serviceFilter,
        sub_service: subServiceFilter || undefined,
        // 传递所有筛选条件
        nation: filters.nation || undefined,
        payment_status: filters.payment_status || undefined,
        applicant_name: filters.applicant_name || undefined,
        contact_name: filters.contact_name || undefined,
        channel: filters.channel || undefined,
        source: filters.source || undefined,
        status: filters.status || undefined,
        seller: filters.seller ? Number(filters.seller) : undefined,
        date_field: (filters.date_field || undefined) as OrderListParams['date_field'],
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        // 导出列设置
        columns: (visibleColumns || []).filter((key) => key && key !== 'actions'),
      };

      const blob = await orderService.exportOrders(params);

      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const serviceSuffix = serviceFilter ? `_${serviceFilter}` : '';
      link.download = `订单数据${serviceSuffix}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败');
    }
  };

  // 处理导入
  const handleImport = async (file: any) => {
    setImportLoading(true);
    setImportProgress(0);

    try {
      const result = await orderService.importOrders(file, serviceFilter || undefined);

      if (result.success) {
        const { total, success, updated, failed, errors } = result.data;

        let messageText = `导入完成！总计: ${total}, 成功: ${success}, 更新: ${updated}`;
        if (failed > 0) {
          messageText += `, 失败: ${failed}`;
        }

        message.success(messageText);

        // 如果有错误，显示详细错误信息
        if (errors && errors.length > 0) {
          console.error('导入错误详情:', errors);
          Modal.error({
            title: '部分数据导入失败',
            content: (
              <div>
                <p>以下数据导入失败，请检查后重新导入：</p>
                <ul style={{ maxHeight: '200px', overflow: 'auto' }}>
                  {errors.slice(0, 5).map((error: any, index: number) => (
                    <li key={index}>第{error.row}行: {error.error}</li>
                  ))}
                  {errors.length > 5 && <li>... 还有 {errors.length - 5} 个错误</li>}
                </ul>
              </div>
            ),
            width: 600
          });
        }

        // 重新加载订单列表
        fetchOrders();
      } else {
        message.error(result.message || '导入失败');
      }
    } catch (error) {
      console.error('导入失败:', error);
      message.error('导入失败，请检查文件格式');
    } finally {
      setImportLoading(false);
      setImportProgress(0);
      setImportModalVisible(false);
    }

    return false; // 阻止默认上传行为
  };


  // 表格分页变化
  const handleTableChange = async (paginationConfig: any) => {
    const isPageSizeChanged = pagination.pageSize !== paginationConfig.pageSize;

    // 如果每页条数发生变化，保存用户偏好并从第一页开始
    if (isPageSizeChanged) {
      try {
        await userPreferenceService.savePageSizeSettings('orders', paginationConfig.pageSize, serviceFilter || undefined);
      } catch (error) {
        console.error('保存每页条数设置失败:', error);
      }

      const newPagination = {
        ...pagination,
        current: 1, // 从第一页开始
        pageSize: paginationConfig.pageSize,
      };
      setPagination(newPagination);
      updateUrlParams(newPagination);
    } else {
      // 只是翻页，直接更新页码
      const newPagination = {
        ...pagination,
        current: paginationConfig.current,
      };
      setPagination(newPagination);
      updateUrlParams(newPagination);
    }
  };

  // 处理删除、添加监控 已提前上移（避免初始化前被引用）
  // 原地删除重复定义

  // 由于使用API搜索，直接使用orders数据
  return (
    <div style={{ padding: '24px' }}>
      {/* 页面标题或提示 */}
      {!serviceFilter && (
        <Card style={{ marginBottom: 16, textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 16, color: '#999', marginBottom: 16 }}>
            请从左侧菜单选择要管理的业务类型
          </div>
          <div style={{ color: '#666' }}>
            选择商标服务、专利服务、版权服务等来查看对应的订单列表
          </div>
        </Card>
      )}


      {serviceFilter && !hasCurrentServiceAccess && (
        <Card style={{ marginBottom: 16, textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 16, color: '#999', marginBottom: 16 }}>
            无权限访问该业务订单子菜单
          </div>
          <div style={{ color: '#666' }}>
            请联系管理员调整业务订单页面访问权限
          </div>
        </Card>
      )}

      {serviceFilter && hasCurrentServiceAccess && (
        <Card
          className="orders-page-card"
          title={(
            <Space size="small">
              <span>{serviceLabel ? serviceLabel : '所有订单'}</span>
              <ListHelpButton page_key="order_management" />
            </Space>
          )}
          extra={
            <Space>
              {renderColumnSelector()}
              {canCreate && serviceFilter && serviceFilter !== 'all' && serviceFilter !== 'madrid' && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => handleServiceSelect(serviceFilter || 'trademark')}
                  data-testid="btn-order-add"
                >
                  添加订单
                </Button>
              )}
              {serviceFilter === 'madrid' && canCreate && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setBatchModalVisible(true)}>
                  添加马德里订单
                </Button>
              )}
              {(() => {
                const items: any[] = [];
                if (canExport) items.push({ key: 'export', label: '导出数据', icon: <DownloadOutlined />, onClick: handleExport });
                if (canImport) items.push({ key: 'import', label: '导入数据', icon: <UploadOutlined />, onClick: () => setImportModalVisible(true) });
                return items.length > 0 ? (
                  <Dropdown menu={{ items }}>
                    <Button data-testid="btn-orders-data-menu">数据管理</Button>
                  </Dropdown>
                ) : null;
              })()}

              {canDelete && selectedRowKeys.length > 0 && (
                <Button
                  type="primary"
                  onClick={() => setBatchEditModalVisible(true)}
                >
                  批量编辑
                </Button>
              )}

              {canDelete && selectedRowKeys.length > 0 && (
                <Popconfirm
                  title={`确定删除选中的 ${selectedRowKeys.length} 条订单吗？`}
                  description="删除后不可恢复，请谨慎操作"
                  okText="确定"
                  cancelText="取消"
                  onConfirm={handleBulkDelete}
                >
                  <Button danger>批量删除</Button>
                </Popconfirm>
              )}
            </Space>
          }
        >
          {serviceFilter !== 'other' && (
            <SubServiceFilter
              options={subServiceOptions as any}
              value={subServiceFilter}
              onChange={handleSubServiceChange}
            />
          )}
          {/* 搜索和操作区域 */}
          <OrdersToolbar
            searchText={searchText}
            searchField={searchField}
            searchFieldOptions={searchFieldOptions}
            onSearchTextChange={setSearchText}
            onSearchFieldChange={setSearchField}
            filterOptions={filterOptions as any}
            filters={filters as any}
            onFilterChange={(key, val) => handleFilterChange(key as any, val)}
            serviceFilter={serviceFilter || undefined}
            isAdmin={isAdminUser || isManagerUser}
            sellerOptions={sellerOptions}
          />

          <FilterSummary
            searchText={searchText}
            searchField={searchField}
            searchFieldOptions={searchFieldOptions}
            onClearSearch={() => setSearchText('')}
            onClearSearchField={() => setSearchField('all')}
            filters={filters as any}
            filterOptions={filterOptions as any}
            onClearFilter={(key) => handleFilterChange(key as any, '')}
            onResetAll={handleResetFilters}
          />

          {/* 批量删除已移动到卡片标题区域 */}

          <OrdersTable
            columns={columns}
            dataSource={orders}
            loading={loading}
            rowSelection={canDelete ? rowSelection as any : undefined}
            expandable={{
              expandedRowRender: (record: any) => (
                <OrdersTable
                  columns={(record?.order_type === 'madrid' ? childColumns : columns) as any}
                  dataSource={childrenMap[record.id] || []}
                  loading={false}
                  pagination={false as any}
                  sticky={false}
                  onChange={undefined}
                />
              ),
              rowExpandable: (record: any) => !record.parent_id && (record.order_type === 'madrid' || (record.children_count ?? 0) > 0),
              onExpand: (expanded: boolean, record: any) => {
                if (expanded && record?.id && !childrenMap[record.id]) {
                  fetchChildren(record.id);
                }
              },
            }}
            pagination={{
              ...pagination,
              pageSize: pagination.pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            }}
            onChange={(pagination, filters, sorter) => {
              handleTableChange(pagination as any);
              setSortConfig({
                field: Array.isArray(sorter) ? undefined : (sorter.field as string),
                order: Array.isArray(sorter) ? undefined : (sorter.order || undefined),
              });
            }}
            rowKey="id"
          />
        </Card>
      )}

       <Modal
        title={`批量编辑 (${selectedRowKeys.length} 个订单)`}
        open={batchEditModalVisible}
        onOk={handleBatchEdit}
        onCancel={() => {
          setBatchEditModalVisible(false);
          batchEditForm.resetFields();
        }}
        width={600}
        okText="确定"
        cancelText="取消"
      >
        <Form form={batchEditForm} layout="vertical">
          <Alert
            message="只填写需要修改的字段，未填写的字段将保持原值不变"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item label="付款状态" name="payment_status">
            <Select placeholder="选择付款状态（不修改请留空）" allowClear>
              <Select.Option value="已付款">已付款</Select.Option>
              <Select.Option value="未付款">未付款</Select.Option>
            </Select>
          </Form.Item>

          {isAdminUser && (
            <Form.Item label="所属销售" name="seller">
              <Select
                placeholder="选择所属销售（不修改请留空）"
                allowClear
                options={sellerOptions}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          )}

          <Form.Item label="付款日期" name="payment_at">
            <DatePicker style={{ width: '100%' }} placeholder="选择付款日期（不修改请留空）" />
          </Form.Item>
          <Form.Item label="回执日期" name="receipt_at">
            <DatePicker style={{ width: '100%' }} placeholder="选择回执日期（不修改请留空）" />
          </Form.Item>
          <Form.Item label="注册日期" name="approve_at">
            <DatePicker style={{ width: '100%' }} placeholder="选择批准日期（不修改请留空）" />
          </Form.Item>
          <Form.Item label="截止日期" name="due_at">
            <DatePicker style={{ width: '100%' }} placeholder="选择到期日期（不修改请留空）" />
          </Form.Item>
          <Form.Item label="异常日期" name="expiration_at">
            <DatePicker style={{ width: '100%' }} placeholder="选择失效日期（不修改请留空）" />
          </Form.Item>

        </Form>
      </Modal>


      {/* 订单编辑Modal */}
      <Modal
        title={
          editingOrder
            ? '编辑订单'
            : selectedServiceType
              ? `新增${SERVICE_TYPES.find(s => s.value === selectedServiceType)?.label || '订单'}`
              : '新增订单'
        }
        open={modalVisible}
        onOk={handleSave}
        onCancel={handleCancel}
        width={1000}
        destroyOnClose
        maskClosable={false}
        data-testid="modal-order"
        okButtonProps={{ 'data-testid': 'btn-order-save' } as any}
        cancelButtonProps={{ 'data-testid': 'btn-order-cancel' } as any}
      >
        {selectedServiceType && (
          <Row gutter={24}>
            <Col span={5}>
              {(() => {
                const isMadridEdit = !!editingOrder && editingOrder.order_type === 'madrid';

                const items = [
                  { key: 'business', href: '#section-business', title: '基本信息' },
                ] as any[];

                const showApplicant = watchedSubService !== 'search' && selectedServiceType !== 'company' && selectedServiceType !== 'barcode' && selectedServiceType !== 'other'

                if (showApplicant) {
                  items.push({ key: 'applicant', href: '#section-applicant', title: '申请人信息' });
                }

                if (isMadridEdit) items.push({ key: 'nations', href: '#section-nations', title: '指定国家' });

                items.push(
                  { key: 'client', href: '#section-client', title: '客户信息' },
                  { key: 'price', href: '#section-price', title: '财务信息' },
                  { key: 'files', href: '#section-files', title: '文件' },
                );
                return (
                  <Anchor
                    items={items}
                    affix
                    targetOffset={8}
                    getContainer={() => modalContentRef.current as unknown as HTMLElement}
                  />
                );
              })()}
            </Col>
            <Col span={19}>
              <div ref={modalContentRef} style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
                <Form
                  form={form}
                  layout="vertical"
                  initialValues={{
                    service: selectedServiceType,
                    client_role: 'client',
                    payment_status: '未付款',
                    // 默认子业务：
                    // 1) other 服务默认 registration
                    ...(selectedServiceType === 'other' ? { sub_service: 'registration' } : {}),
                    // 2) 若当前列表通过 URL 指定了 sub_service，且属于当前服务，则优先选中
                    ...(() => {
                      try {
                        if (!selectedServiceType) return {};
                        const urlSub = subServiceFilter;
                        if (!urlSub) return {};
                        const map = (SERVICE_TYPE_MAP as any)[selectedServiceType] || {};
                        return Object.prototype.hasOwnProperty.call(map, urlSub) ? { sub_service: urlSub } : {};
                      } catch (e) {
                        return {};
                      }
                    })(),
                  }}
                >
                  {/* 隐藏的files字段，用于存储文件ID字符串 */}
                  <Form.Item name="files" hidden />

                  <DynamicOrderForm
                    serviceType={selectedServiceType}
                    form={form}
                    isEdit={!!editingOrder}
                    anchorMode
                    // 马德里父/子单启用基础国家模式；或当用户选择了“马德里商标”子业务时
                    baseCountryMode={selectedServiceType === 'trademark' && (!!editingOrder
                      ? (editingOrder.order_type === 'madrid' || editingOrder.order_type === 'madrid_child')
                      : false)}
                    // 当编辑马德里父单时，提供与“+马德里”一致的锚点分节（含“指定国家”区块）
                    extraTabsAfterBasic={(() => {
                      const isMadrid = selectedServiceType === 'trademark' && (!!editingOrder && editingOrder.order_type === 'madrid');
                      if (!isMadrid) return undefined as any;
                      return {
                        key: 'nations',
                        label: '指定国家',
                        children: (
                          <div>
                            <div style={{ marginBottom: 8, color: '#666' }}>选择要创建子订单的国家：</div>
                            <Select
                              mode="multiple"
                              placeholder="选择国家"
                              style={{ width: '100%' }}
                              value={batchSelectedNations}
                              onChange={(vals) => setBatchSelectedNations(vals as string[])}
                              options={NATION_OPTIONS}
                            />
                            <div style={{ marginTop: 16 }}>
                              <Form.Item label="国际注册号" name="international_registration_no">
                                <Input placeholder="请输入国际注册号（可选）" />
                              </Form.Item>
                            </div>
                          </div>
                        ),
                      } as any;
                    })()}
                  />
                </Form>
              </div>
            </Col>
          </Row>
        )}
      </Modal>

      <ImportOrdersModal
        open={importModalVisible}
        loading={importLoading}
        progress={importProgress}
        onClose={() => { setImportModalVisible(false); setImportProgress(0); }}
        onImport={handleImport}
      />

      {/* 更新状态弹窗 */}
      <Modal
        title="更新状态"
        open={statusModalVisible}
        onCancel={() => { setStatusModalVisible(false); statusForm.resetFields(); }}
        onOk={handleSaveStatus}
        destroyOnHidden
        className="update-status-modal"
        width={900}
      >
        {/* 顶部：步骤型进度（根据 sub_service 的步骤映射） */}
        <div style={{ marginBottom: 16, overflowX: 'auto' }}>
          <Steps
            style={{ width: 'max-content', minWidth: '100%' }}
            size="small"
            current={Math.max(0, (statusHistory || []).filter(s => s.status).length - 1)}
            labelPlacement="vertical"
            items={((): any[] => {
              const order = currentStatusOrderRef.current;
              const ext = order && order?.service ? (order[order.service as keyof Order] as any) : undefined;
              const subService = ext?.sub_service;
              const full = getStatusOptionsBySubService(order?.service || 'trademark', subService, statusOptionsMap);
              const labelMap = new Map(full.map(opt => [opt.value, opt.label]));
              const seen = new Set<string>();
              const historyList = (statusHistory || []).filter(s => {
                const key = s.status || '';
                if (!key) return false;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              });
              return historyList.map((item, index) => {
                const title = labelMap.get(item.status || '') || item.status || '-';
                const st = index === historyList.length - 1 ? 'process' : 'finish';
                const finishedAt = statusFinishTimeMap[item.status || ''];
                return {
                  title,
                  status: st as any,
                  description: (
                    <div style={{ textAlign: 'center' }}>
                      {finishedAt ? <div>{finishedAt}</div> : null}
                      {item.id ? (
                        <Popconfirm
                          title="确认删除该状态记录？"
                          okText="删除"
                          cancelText="取消"
                          onConfirm={() => handleDeleteStatus(item.id!)}
                        >
                          <Button type="link" size="small" danger>删除</Button>
                        </Popconfirm>
                      ) : null}
                    </div>
                  ),
                };
              });
            })()}
          />
        </div>

        {/* 下方：左右布局（左：订单信息 | 右：状态更新表单） */}
        <Row gutter={16}>
          <Col span={12}>
            {(() => {
              const order = currentStatusOrderRef.current as any;
              const service = order?.service;
              const ext = order && service ? (order[service] || {}) : {};
              const subServiceLabel = service && ext?.sub_service ? (SERVICE_TYPE_MAP as any)?.[service]?.[ext.sub_service] : undefined;
              const applicationNo = ext?.application_no || order?.application_no || '-';
              const applicantName = order?.applicant_name || ext?.applicant_name || '-';
              const serviceLabel = (() => {
                switch (service) {
                  case 'trademark':
                    return '商标名';
                  case 'patent':
                    return '专利名称';
                  case 'copyright':
                    return '作品名';
                  case 'company':
                  case 'barcode':
                    return '公司名称';
                  case 'other':
                    return '业务名';
                  default:
                    return order?.service_name;
                }
              })() || '-';
              const serviceName = (() => {
                switch (service) {
                  case 'trademark':
                    return ext?.trademark_name;
                  case 'patent':
                    return ext?.patent_name;
                  case 'copyright':
                    return ext?.work_name;
                  case 'company':
                  case 'barcode':
                    return ext?.company_name;
                  case 'other':
                    return ext?.service_name || order?.service_name;
                  default:
                    return order?.service_name;
                }
              })() || '-';
              return (
                <Descriptions bordered size="small" column={1} title="订单信息">
                  <Descriptions.Item label="订单编号">{order?.order_no || '-'}</Descriptions.Item>
                  <Descriptions.Item label="子业务类型">{subServiceLabel || ext?.sub_service || '-'}</Descriptions.Item>
                  <Descriptions.Item label={serviceLabel}>{serviceName}</Descriptions.Item>
                  <Descriptions.Item label="国家/地区">{order?.nation || '-'}</Descriptions.Item>
                  { service !== 'company' &&
                    <Descriptions.Item label="申请号">{applicationNo}</Descriptions.Item>
                  }
                  { service === 'company' &&
                    <Descriptions.Item label="公司编号">{ext?.company_no}</Descriptions.Item>
                  }
                  <Descriptions.Item label="申请人名字">{applicantName}</Descriptions.Item>
                  <Descriptions.Item label="客户名">{order?.contact_name}</Descriptions.Item>
                </Descriptions>
              );
            })()}
          </Col>
          <Col span={12}>
            <Form form={statusForm} layout="vertical">
              <Form.Item label="最新状态" name="status">
                <Select options={statusOptionsForModal} placeholder={statusOptionsForModal.length ? '请选择状态' : '无可选状态'} disabled={!statusOptionsForModal.length} />
              </Form.Item>
              {(() => {
                const order = currentStatusOrderRef.current as any;
                const service = order?.service;
                const sub_service = order && service ? ((order as any)[service]?.sub_service) : undefined;
                let hideNext = false;

                if (service === 'patent') {
                  hideNext = ['rejection_review'].includes(sub_service);
                } else if (service === 'trademark'){
                  hideNext = ['extension','postpone_evidence','review','revocation','search','cancellation','certificate_reissue'].includes(sub_service);
                } else if (service === 'company'){
                  hideNext = ['bank_account_opening','information_changes','cancellation'].includes(sub_service);
                } else if (service === 'barcode'){
                  hideNext = ['cancellation'].includes(sub_service);
                } else if (service === 'other'){
                  hideNext = true;
                }

                if (hideNext) return null;
                return (
                  <Form.Item label="下个状态" name="next_status">
                    <Select
                      placeholder="请选择下个状态"
                      options={nextStatusOptions}
                      allowClear
                      showSearch
                    />
                  </Form.Item>
                );
              })()}
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item label="更新时间" name="created_at" rules={[{ required: true, message: '请选择更新时间' }]}>
                    <DatePicker style={{ width: '100%' }} showTime format="YYYY-MM-DD HH:mm" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="截止日期" name="due_at">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="订单异常日期" name="expiration_at">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="申请号" name="application_no">
                    <Input placeholder="请输入申请号" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="注册号" name="registration_no">
                    <Input placeholder="请输入注册号" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="备注" name="remark">
                <Input.TextArea rows={2} placeholder="请输入备注信息" />
              </Form.Item>
              <Form.Item label="文件">
                <Form.Item noStyle name="files" initialValue={[]}>
                  <MediaSelector
                    multiple
                    placeholder="选择或上传文件"
                    fileTypes={['image', 'document', 'video', 'audio', 'other']}
                    showPreview
                    value={statusForm.getFieldValue('files') || []}
                    onChange={(selectedIds?: number | number[]) => {
                      const arr = Array.isArray(selectedIds) ? selectedIds : (selectedIds ? [selectedIds] : []);
                      statusForm.setFieldsValue({ files: arr });
                    }}
                  />
                </Form.Item>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Modal>

      {/* 批量新增（马德里） */}
      {serviceFilter === 'madrid' && (
        <Modal
          title="批量新增（马德里）"
          open={batchModalVisible}
          onCancel={() => { setBatchModalVisible(false); setBatchSelectedNations([]); batchForm.resetFields(); setBatchTab('parent'); }}
          onOk={async () => {
            try {
              const values = await batchForm.validateFields();
              // 计算提成和利润
              const calc = await CommissionCalculator.calculate({ price: values.price, cost: values.cost, client_type: values.client_type });

          const parentData: any = {
            ...values,
            service: 'trademark',
            sub_service: (values?.sub_service || 'registration'),
            profit: calc.profit?.toString?.() ?? values.profit,
            commission: calc.commission?.toString?.() ?? values.commission,
            due_at: values.due_at?.format?.('YYYY-MM-DD HH:mm:ss'),
            payment_at: values.payment_at?.format?.('YYYY-MM-DD HH:mm:ss'),
            created_at: values.created_at?.format?.('YYYY-MM-DD HH:mm:ss'),
            order_type: 'madrid',
          };

              // 规范化：primary_class 多选（数组）转字符串，避免后端 Array to string 错误
              if (Array.isArray(values.primary_class)) {
                parentData.primary_class = values.primary_class.join(',');
              }

              const parentRes = await orderService.createOrder(parentData);
              if (!parentRes.success || !parentRes.data?.id) {
                message.error(parentRes.message || '父订单创建失败');
                return;
              }

              const parentId = parentRes.data.id;

              // 使用父订单的业务类型获取订单编号前缀
              const orderNoPrefix = getOrderPrefix(parentData.service || 'trademark', parentData.sub_service || 'registration');

              // 创建子订单
              for (const nation of batchSelectedNations) {
                const childData = {
                  ...parentData,
                  id: undefined,
                  parent_id: parentId,
                  nation,
                  // 使用父订单数据获取的前缀生成订单编号
                  order_no: generateOrderNoWithPrefix(orderNoPrefix),
                  registration_no: values.international_registration_no,
                  order_type: 'madrid_child',
                } as any;
                try {
                  // 单独计算每个子单的提成/利润（可与父同）
                  await orderService.createOrder(childData);
                } catch (e) {
                  console.warn('创建子订单失败', nation, e);
                }
              }

              message.success('批量添加成功');
              setBatchModalVisible(false);
              setBatchSelectedNations([]);
              batchForm.resetFields();
              fetchOrders();
            } catch (err: any) {
              message.error(err?.response?.data?.message || '保存失败');
            }
          }}
          width={1000}
          destroyOnHidden
          maskClosable={false}
        >
          <Row gutter={24}>
            <Col span={5}>
              <Anchor
                items={(() => {
                  const baseItems = [
                    { key: 'business', href: '#section-business', title: '基本信息' },
                  ];
                  if (batchWatchedSubService !== 'search') {
                    baseItems.push({ key: 'applicant', href: '#section-applicant', title: '申请人信息' });
                  }
                  baseItems.push(
                    { key: 'nations', href: '#section-nations', title: '指定国家' },
                    { key: 'client', href: '#section-client', title: '客户信息' },
                    { key: 'price', href: '#section-price', title: '财务信息' },
                    { key: 'files', href: '#section-files', title: '文件' },
                  );
                  return baseItems;
                })()}
                affix
                targetOffset={8}
                getContainer={() => batchModalContentRef.current as unknown as HTMLElement}
              />
            </Col>
            <Col span={19}>
              <div ref={batchModalContentRef} style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
                <Form form={batchForm} layout="vertical" initialValues={{ service: 'trademark', sub_service: 'registration' }}>
                  <Form.Item name="files" hidden />
                  <DynamicOrderForm
                    serviceType={'trademark'}
                    form={batchForm}
                    isEdit={false}
                    baseCountryMode
                    anchorMode
                    extraTabsAfterBasic={{
                      key: 'nations',
                      label: '指定国家',
                      children: (
                        <div>
                          <div style={{ marginBottom: 8, color: '#666' }}>选择要创建子订单的国家：</div>
                          <Select
                            mode="multiple"
                            placeholder="选择国家"
                            style={{ width: '100%' }}
                            value={batchSelectedNations}
                            onChange={(vals) => setBatchSelectedNations(vals as string[])}
                            options={NATION_OPTIONS}
                          />
                          <div style={{ marginTop: 16 }}>
                            <Form.Item label="国际注册号" name="international_registration_no">
                              <Input placeholder="请输入国际注册号（可选）" />
                            </Form.Item>
                          </div>
                        </div>
                      ),
                    }}
                  />
                </Form>
              </div>
            </Col>
          </Row>
        </Modal>
      )}
    </div>
  );
};

export default OrderManagement;
